import React, { memo } from 'react'
import { useDispatch, useSelector } from '@tabeeb/store'

import * as Yup from 'yup'
import { Field, Form, Formik } from 'formik'

import { Box } from '@mui/material'

import { UserRole } from '@tabeeb/enums'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, withModernTheme } from '@collaborate-health/uikit'

import { getQualificationsForInvitesEnabled } from '@tabeeb/modules/appConfigState/selectors'
import { QualificationsAutocomplete } from '@tabeeb/modules/qualifications/components/Fields'
import { UserRoleSelectField } from '@tabeeb/modules/invites/forms'
import UsersMultiSelectWithQualifications from '@tabeeb/modules/invites/components/UsersMultiSelectWithQualifications'
import { validateEmail, validatePhone } from '@tabeeb/modules/../users/services/users'

import { ContentUserRole } from '@tabeeb/contracts/main-api/swagger-main-api'
import { FormAutocompleteProps } from '@collaborate-health/forms'
import { addUsersToSelectedSessions } from '../../../actions'

const schema = Yup.object().shape({
  Role: Yup.number().strict().oneOf([UserRole.User, UserRole.Viewer, UserRole.SessionGuest]).required(),
  Users: Yup.array()
    .strict()
    .of(
      Yup.object({
        Email: Yup.string()
          .strict()
          .test('email_or_phone', 'Email / Phone is invalid', (value) => {
            return validateEmail(value) || validatePhone(value)
          }),
      }).required()
    )
    .min(1)
    .required(),
})

const initial = {
  Role: ContentUserRole.User,
  Users: [],
  Qualifications: [],
}

interface AddUsersToSessionsDialogProps {
  open: boolean
  onClose: () => void
  sessions: {
    Id: number
    Description: string
  }[]
}

const AddUsersToSessionsDialog: React.FC<AddUsersToSessionsDialogProps> = ({ open, sessions, onClose }) => {
  const dispatch = useDispatch()
  const qualificationsForInvitesEnabled = useSelector(getQualificationsForInvitesEnabled)

  const onSubmit = ({
    Users,
    Role,
    Qualifications,
  }: {
    Users: { Id: number }[]
    Role: ContentUserRole
    Qualifications: FormAutocompleteProps<number>['options']
  }) => {
    const request = {
      UserIds: Users.map((user) => user.Id),
      QualificationIds: Qualifications?.map((option) => option.value),
      RoleId: Role,
    }
    dispatch(addUsersToSelectedSessions(request))

    onClose()
  }

  return (
    <Dialog open={open} fullWidth maxWidth='sm' onClose={onClose}>
      <Formik initialValues={initial} validateOnMount validationSchema={schema} onSubmit={onSubmit} enableReinitialize>
        {({ isValid, dirty }) => (
          <Form autoComplete='off'>
            <DialogTitle>Add users</DialogTitle>
            <DialogContent>
              {qualificationsForInvitesEnabled && (
                <Box mb={1} width='100%'>
                  <QualificationsAutocomplete />
                </Box>
              )}
              <Field
                name='Users'
                component={UsersMultiSelectWithQualifications}
                endAdornment={<Field name='Role' component={UserRoleSelectField} />}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Close</Button>
              <Button disabled={!dirty || !isValid} color='primary' variant='contained' type='submit'>
                Add users
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default memo<AddUsersToSessionsDialogProps>(withModernTheme(AddUsersToSessionsDialog))

